import { useLocation } from 'react-router-dom'
import { useQuery } from '../../../routes'
import { humanize, useDebounce } from 'utils'
import { flat } from 'utils'
import { useSearchEvents, AlgoliaEvent } from '../../../queries/useSearchEvents'
import { Event } from '../../../queries/types'
import { useState, useEffect, useMemo } from 'react'

/**
 * Custom hook for paginated admin event search using the new useSearchEvents hook
 * with support for temporary filter state and adminSearch=true
 */
export const useAdminEvents = (options: { pageSize?: number } = {}) => {
  const pageSize = options.pageSize ?? 12

  // Parse query parameters from URL
  const query = useQuery<{
    page?: number
    appliedFacets?: string[]
    queryString?: string
  }>()

  const { search: locationSearch } = useLocation()

  // Ensure we get arrays even if the URL params are unexpected types
  const safeAppliedFacets = () => {
    const facets = query.search.appliedFacets
    return Array.isArray(facets) ? facets : facets ? [facets] : []
  }

  // Temporary state for filters and query that will only be applied when the user clicks "Apply"
  const [tempFacets, setTempFacets] = useState<string[]>(safeAppliedFacets())
  const [tempQueryString, setTempQueryString] = useState<string>(
    query.search.queryString || ''
  )

  // Applied state that's reflected in the URL and used for API calls
  const appliedFacets = safeAppliedFacets()
  const queryString = query.search.queryString || ''

  // Update temp state when URL params change (e.g., on browser navigation)
  useEffect(() => {
    setTempFacets(safeAppliedFacets())
    setTempQueryString(query.search.queryString || '')
  }, [locationSearch])

  // Check if there are unapplied changes
  const hasUnappliedChanges = useMemo(() => {
    // Ensure we're working with arrays
    const tempFacetsArray = Array.isArray(tempFacets) ? tempFacets : []
    const appliedFacetsArray = Array.isArray(appliedFacets) ? appliedFacets : []

    // Check query string differences
    if ((tempQueryString || '') !== (queryString || '')) return true

    // Check facet count differences
    if (tempFacetsArray.length !== appliedFacetsArray.length) return true

    // Check if any facets are different
    const appliedSet = new Set(appliedFacetsArray)
    return tempFacetsArray.some((facet) => !appliedSet.has(facet))
  }, [tempFacets, appliedFacets, tempQueryString, queryString])

  // Handle toggling facets in temporary state
  const toggleFacet = (facet: string, action: 'remove' | 'add') => {
    // Ensure we're working with an array
    const safeTempFacets = Array.isArray(tempFacets) ? tempFacets : []

    let updatedFacets
    if (action === 'remove') {
      // Remove the facet
      updatedFacets = safeTempFacets.filter((f) => f !== facet)
    } else {
      // Add the facet if it doesn't already exist
      if (!safeTempFacets.includes(facet)) {
        updatedFacets = [...safeTempFacets, facet]
      } else {
        updatedFacets = [...safeTempFacets] // Keep as is if already exists
      }
    }

    setTempFacets(updatedFacets)
  }

  // Update temporary search query string
  const setTempSearchQuery = (newQueryString: string) => {
    setTempQueryString(newQueryString)
  }

  // Apply filters to URL and trigger search
  const applyFilters = () => {
    // Ensure we're sending valid data to URL
    const safeTempFacets = Array.isArray(tempFacets) ? tempFacets : []

    query.set({
      ...query.search,
      appliedFacets: safeTempFacets,
      queryString: tempQueryString || '',
      page: 1, // Reset to first page when applying new filters
    })
  }

  // Reset filters to match URL state
  const resetFilters = () => {
    setTempFacets(safeAppliedFacets())
    setTempQueryString(query.search.queryString || '')
  }

  // Clear all filters (both temp and applied)
  const clearAllFilters = () => {
    // Clear temporary state
    setTempFacets([])
    setTempQueryString('')

    // Clear applied state (URL parameters)
    query.set({
      page: 1,
      appliedFacets: [],
      queryString: '',
    })
  }

  // Debounce applied query for API calls
  const requestedQuery = useDebounce(queryString, 450)

  // Search parameters
  const searchParams = {
    query: requestedQuery,
    facet_filters: Array.isArray(appliedFacets)
      ? appliedFacets
      : [appliedFacets], // Ensure we're passing an array
    page: (query.search.page ?? 1) - 1, // Convert from 1-indexed to 0-indexed
    limit: pageSize,
    adminSearch: true, // Set adminSearch to true
    includeInvited: true,
    includeCreated: true,
    includeOpen: true,
  }

  // Use our event search hook with the applied (not temporary) filters
  const { data, isLoading: loading, error } = useSearchEvents(searchParams)

  // Handle page changes
  const handlePageClick = ({ historyPush }: { historyPush?: boolean }) => (
    page: number
  ) => {
    query.set({ ...query.search, page }, historyPush)
  }

  // Format applied facets for display (using the applied facets, not temporary)
  const humanizedAppliedFacets = useMemo(() => {
    try {
      const safeArray = Array.isArray(appliedFacets)
        ? appliedFacets
        : [appliedFacets]
      return flat([safeArray], 2)
        .map((f) => {
          // Ensure f is a string before splitting
          if (typeof f !== 'string') return ['', '']
          return f.split(':')
        })
        .map((f) => ({
          id: f.join(':'),
          name: humanize(f[0] || ''),
          value: humanize(f[1] || ''),
        }))
    } catch (e) {
      console.warn('Error formatting facets:', e)
      return []
    }
  }, [appliedFacets])

  // Extract data for the view with thorough safety checks
  try {
    // Make sure events is always an array to prevent filter errors
    const originalItems = data?.items

    // Defensive handling of events data
    let events: AlgoliaEvent[] = []

    if (Array.isArray(originalItems)) {
      events = originalItems
    } else if (originalItems && typeof originalItems === 'object') {
      // If originalItems is an object but not an array, try to extract values
      const possibleArray = Object.values(originalItems)
      events = (Array.isArray(possibleArray)
        ? possibleArray
        : []) as AlgoliaEvent[]
    }

    const currentPage = (data?.page ?? 0) + 1 // Convert from 0-indexed to 1-indexed
    const pageCount = data?.pages ?? 0
    const success = !!data
    const facets = data?.facets || {}

    return {
      // Applied state (from URL)
      appliedFacets,
      queryString,

      // Temporary state
      tempFacets,
      tempQueryString,
      hasUnappliedChanges,

      // Actions
      toggleFacet,
      setTempSearchQuery,
      applyFilters,
      resetFilters,
      clearAllFilters,
      setQueryString: setTempSearchQuery,

      // Search results
      currentPage,
      pageCount,
      error,
      facets,
      loading,
      events,
      search: query.search,
      handlePageClick,
      success,
      locationSearch,
      humanizedAppliedFacets,
    }
  } catch (err) {
    console.error('Error processing event data:', err)
    // Return safe fallback values
    return {
      // Applied state (from URL)
      appliedFacets,
      queryString,

      // Temporary state
      tempFacets,
      tempQueryString,
      hasUnappliedChanges,

      // Actions
      toggleFacet,
      setTempSearchQuery,
      applyFilters,
      resetFilters,
      clearAllFilters,
      setQueryString: setTempSearchQuery,

      // Search results with safe fallbacks
      currentPage: (data?.page ?? 0) + 1,
      pageCount: data?.pages ?? 0,
      error: error || new Error('Error processing event data'),
      facets: data?.facets || {},
      loading,
      events: [] as AlgoliaEvent[],
      search: query.search,
      handlePageClick,
      success: false,
      locationSearch,
      humanizedAppliedFacets,
    }
  }
}
